import { AppDispatch, RootState } from "~/store";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import {App, fetchApps} from "~/store/appSlice.ts";
import {
    useToast,
    Container,
    SimpleGrid,
    Box,
    Image,
    Heading,
    Text,
    VStack,
    Badge,
    useColorModeValue
} from "@chakra-ui/react";
import FullPageLoader from "~/components/functions/FullPageLoader.tsx";
import useExtendedNavigation from "~/hooks/useExtendedNavigation.ts";

const AppCard = ({ name, description, public_id }: App) => {
    const navigate = useExtendedNavigation();
    const cardBg = useColorModeValue('gray.800', 'gray.900');
    const textColor = useColorModeValue('gray.100', 'gray.200');
    const descriptionColor = useColorModeValue('gray.300', 'gray.400');
    const badgeBg = useColorModeValue('gray.700', 'gray.800');

    const handleClick = () => {
        navigate(`/app/${public_id}/login`);
    };

    return (
        <Box
            bg={cardBg}
            borderRadius="xl"
            overflow="hidden"
            transition="transform 0.2s"
            _hover={{
                transform: 'translateY(-4px)',
                cursor: 'pointer',
                boxShadow: '2xl'
            }}
            boxShadow="xl"
            onClick={handleClick}
        >
            <Image
                src="https://via.placeholder.com/400x200"
                alt={name}
                objectFit="cover"
                h="200px"
                w="full"
                bg="gray.700"
            />
            <VStack align="start" p={6} spacing={3}>
                <Heading size="md" color={textColor}>
                    {name}
                </Heading>
                <Badge
                    bg={badgeBg}
                    color={textColor}
                    px={2}
                    py={1}
                    borderRadius="md"
                    fontSize="sm"
                >
                    ID: {public_id}
                </Badge>
                <Text
                    color={descriptionColor}
                    noOfLines={3}
                    fontSize="sm"
                >
                    {description}
                </Text>
            </VStack>
        </Box>
    );
};

const Apps = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { apps, error, loading } = useSelector((state: RootState) => state.app);
    const toast = useToast();

    useEffect(() => {
        if (!apps.length) {
            dispatch(fetchApps());
        }
    }, []);

    useEffect(() => {
        if (error) {
            toast({
                title: 'Error',
                description: error.toString().substring(0, 100) + '...',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    }, [error]);

    if (loading) return <FullPageLoader />;

    return (
        <Container maxW="7xl" py={8}>
            <Heading
                mb={8}
                color={useColorModeValue('gray.100', 'gray.200')}
                size="lg"
            >
                Shopify Applications
            </Heading>
            <SimpleGrid
                columns={{ base: 1, md: 2, lg: 3 }}
                spacing={6}
            >
                {apps.map((app) => (
                    <AppCard
                        key={app.public_id}
                        name={app.name}
                        client_id={app.client_id}
                        description={app.description}
                        public_id={app.public_id}
                    />
                ))}
            </SimpleGrid>
        </Container>
    );
};

export default Apps;